import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoPlayer from "./components/VideoPlayer";
import CommentSection from "./components/CommentSection";
import RelatedList from "./components/RelatedList";
import NotFound from "@/pages/NotFound";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getVideoById, getRelatedVideos, getCategoryById } from "@/lib/videoData";
import { formatCount, formatDate } from "@/lib/format";

export default function DetailPage() {
  const { videoId } = useParams();
  const video = getVideoById(videoId);
  const [expanded, setExpanded] = useState(false);
  const [liked, setLiked] = useState(false);
  const [favored, setFavored] = useState(false);
  const [tip, setTip] = useState("");

  usePageMeta({
    title: video ? `${video.title} - 快手短视频观看与分享` : "视频详情 - 快手短视频平台",
    description: video
      ? `${video.author} 的作品《${video.title}》，来自快手${video.categoryName}频道，${formatCount(video.views)}次播放。`
      : "快手短视频详情页。",
    keywords: video
      ? `快手,${video.title},${video.author},${video.categoryName},短视频`
      : "快手,短视频",
  });

  if (!video) {
    return <NotFound />;
  }

  const category = getCategoryById(video.categoryId);
  const related = getRelatedVideos(video, 5);

  const showTip = (msg: string) => {
    setTip(msg);
    window.setTimeout(() => setTip(""), 2400);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 pt-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <nav className="flex items-center flex-wrap gap-2 text-sm text-foreground-500 mb-5">
            <Link to="/" className="hover:text-primary-600 transition-colors flex items-center gap-1">
              <i className="ri-home-4-line"></i>
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <Link
              to={`/category/${video.categoryId}`}
              className="hover:text-primary-600 transition-colors"
            >
              {video.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-800 line-clamp-1 max-w-[240px]">{video.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0 space-y-5">
              <VideoPlayer cover={video.cover} title={video.title} duration={video.duration} />

              <div className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-6">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 leading-snug">
                  {video.title}
                </h1>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-play-circle-line text-primary-500"></i>
                    {formatCount(video.views)} 次播放
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {formatDate(video.publishDate)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-film-line"></i>
                    {video.duration}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-background-200">
                  <span className="flex items-center gap-2">
                    <span className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                      <img
                        src={video.authorAvatar}
                        alt={video.author}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span className="text-sm font-medium text-foreground-900">{video.author}</span>
                  </span>
                  <div className="flex items-center gap-2 ml-auto">
                    {category && (
                      <Link
                        to={`/category/${category.id}`}
                        className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                      >
                        <i className={category.icon}></i>
                        {category.name}
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => setLiked((v) => !v)}
                      className={`flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        liked
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                      {formatCount(video.likes + (liked ? 1 : 0))}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFavored((v) => !v)}
                      className={`flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        favored
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      <i className={favored ? "ri-star-fill" : "ri-star-line"}></i>
                      收藏
                    </button>
                    <button
                      type="button"
                      onClick={() => showTip("演示功能：分享链接未接入真实服务")}
                      className="flex items-center gap-1.5 h-10 px-4 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                </div>

                {tip && (
                  <p className="text-xs text-accent-600 mt-3 flex items-center gap-1 animate-fade-in">
                    <i className="ri-information-line"></i>
                    {tip}
                  </p>
                )}

                <div className="mt-4 pt-4 border-t border-background-200">
                  <p
                    className={`text-sm text-foreground-700 leading-relaxed ${
                      expanded ? "" : "line-clamp-2"
                    }`}
                  >
                    {video.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-sm text-primary-600 hover:text-primary-700 mt-2 flex items-center gap-1 cursor-pointer"
                  >
                    {expanded ? "收起简介" : "展开完整简介"}
                    <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                  </button>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {video.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/search?q=${encodeURIComponent(tag)}`}
                        className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-100 hover:text-primary-700 transition-colors"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <CommentSection count={video.comments} />
            </div>

            <aside className="w-full lg:w-[360px] shrink-0">
              <RelatedList videos={related} />
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}